import { useState, useEffect, useCallback } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import { Send, AlertTriangle, Tag, User } from 'lucide-react';
import { logger } from '../../utils/logger';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SupportTicketDetail() {
  const { ticketId } = useParams();
  const { agent } = useOutletContext();
  const [ticket, setTicket] = useState(null);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);

  const fetchTicket = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/support/tickets/${ticketId}`, { credentials: 'include' });
      if (res.ok) setTicket(await res.json());
    } catch (err) { logger.error("Operation failed:", err); }
  }, [ticketId]);

  useEffect(() => { fetchTicket(); }, [fetchTicket]);


  const sendReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await fetch(`${API}/api/support/tickets/${ticketId}/reply`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: reply })
      });
      setReply('');
      fetchTicket();
    } catch (err) { logger.error("Operation failed:", err); }
    setSending(false);
  };

  const updateStatus = async (status) => {
    await fetch(`${API}/api/support/tickets/${ticketId}/status`, {
      method: 'PUT', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status })
    });
    fetchTicket();
  };

  const escalate = async () => {
    await fetch(`${API}/api/support/tickets/${ticketId}/escalate`, {
      method: 'POST', credentials: 'include'
    });
    fetchTicket();
  };

  if (!ticket) return <div className="text-white/50">Loading ticket...</div>;

  return (
    <div data-testid="support-ticket-detail" className="flex gap-6 h-[calc(100vh-8rem)]">
      {/* Thread */}
      <div className="flex-1 flex flex-col">
        <div className="mb-4">
          <h2 className="text-xl font-bold text-white">{ticket.subject}</h2>
          <div className="flex gap-2 mt-2">
            <span className="text-xs bg-white/10 text-white/40 px-2 py-0.5 rounded">{ticket.category}</span>
            <span className="text-xs bg-white/10 text-white/40 px-2 py-0.5 rounded">{ticket.status?.replace('_', ' ')}</span>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto space-y-3 mb-4">
          {(ticket.messages || []).map((m) => (
            <div key={m.id || `msg-${m.created_at}`} className={`flex ${m.author_type === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[70%] p-3 rounded-xl text-sm ${
                m.is_internal_note ? 'bg-amber-500/10 border border-amber-500/20 text-amber-200' :
                m.author_type === 'user' ? 'bg-white/10 text-white' : 'bg-cyan-500/10 text-cyan-100'
              }`}>
                {m.is_internal_note && <span className="text-xs text-amber-400 block mb-1">Internal Note</span>}
                <p>{m.message}</p>
                <p className="text-white/30 text-xs mt-1">{m.created_at?.slice(0, 16).replace('T', ' ')}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="flex gap-2">
          <input
            data-testid="support-reply-input"
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && sendReply()}
            placeholder="Type your reply..."
            className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-cyan-500/50"
          />
          <button
            data-testid="support-reply-send"
            onClick={sendReply}
            disabled={sending || !reply.trim()}
            className="px-4 bg-cyan-600 text-white rounded-xl hover:bg-cyan-700 disabled:opacity-30"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Sidebar */}
      <div className="w-72 shrink-0 space-y-4">
        {ticket.user_profile && (
          <div className="bg-[#0E1830] border border-white/10 rounded-xl p-4">
            <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2"><User className="w-4 h-4" /> User Profile</h3>
            <div className="space-y-2 text-xs">
              <div className="flex justify-between"><span className="text-white/40">Name</span><span className="text-white">{ticket.user_profile.name}</span></div>
              <div className="flex justify-between"><span className="text-white/40">Email</span><span className="text-white truncate ml-2">{ticket.user_profile.email}</span></div>
              <div className="flex justify-between"><span className="text-white/40">PI Checks</span><span className="text-white">{ticket.user_profile.total_pi_checks}</span></div>
              <div className="flex justify-between"><span className="text-white/40">Plan</span><span className="text-white capitalize">{ticket.user_profile.plan}</span></div>
            </div>
          </div>
        )}

        <div className="bg-[#0E1830] border border-white/10 rounded-xl p-4 space-y-2">
          <h3 className="text-white font-semibold text-sm mb-2">Actions</h3>
          {['open', 'in_progress', 'awaiting_user', 'resolved', 'closed'].map(s => (
            <button key={s} onClick={() => updateStatus(s)}
              className={`w-full text-left px-3 py-1.5 rounded text-xs transition-colors ${ticket.status === s ? 'bg-cyan-500/20 text-cyan-400' : 'text-white/40 hover:text-white hover:bg-white/5'}`}
            >
              {s.replace('_', ' ')}
            </button>
          ))}
          <button onClick={escalate} className="w-full text-left px-3 py-1.5 rounded text-xs text-red-400 hover:bg-red-500/10 flex items-center gap-1 mt-2">
            <AlertTriangle className="w-3 h-3" /> Escalate to Admin
          </button>
        </div>

        {ticket.previous_tickets?.length > 0 && (
          <div className="bg-[#0E1830] border border-white/10 rounded-xl p-4">
            <h3 className="text-white font-semibold text-sm mb-2">Previous Tickets ({ticket.previous_tickets.length})</h3>
            {ticket.previous_tickets.slice(0, 5).map(t => (
              <div key={t.id} className="text-xs text-white/40 py-1 border-b border-white/5">{t.subject} — {t.status}</div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
